<template>
  <div class="member-family mt-4">
    <h4 class="benefits-title">
      <i class="fas fa-users mr-2"></i>
      Tu grupo familiar
    </h4>
    <b-alert v-if="errorMessage" show variant="danger" dismissible class="mt-2" @dismissed="errorMessage = null">
      {{ errorMessage }}
    </b-alert>
    <b-alert v-if="pendingInvoiceUrl" show variant="warning" class="mt-2">
      El cobro del integrante nuevo no pasó. El alta queda pendiente hasta que se pague.
      <div class="mt-2">
        <b-button variant="warning" :href="pendingInvoiceUrl" target="_blank" rel="noopener">
          Pagar prorrateo
        </b-button>
      </div>
    </b-alert>
    <b-spinner v-if="loading" small class="my-2" />
    <ul v-else class="list-unstyled mb-3">
      <li v-for="member in members" :key="member.membership_id" class="d-flex justify-content-between align-items-center border-bottom py-2">
        <div>
          <strong>{{ member.name }} {{ member.lastname }}</strong>
          <span class="text-muted small d-block">
            {{ audienceLabel(member) }}
            <span v-if="member.pending_payment"> · pago pendiente</span>
            <span v-if="member.removed_at"> · dado de baja</span>
          </span>
        </div>
        <b-button
          v-if="canRemove(member)"
          size="sm"
          variant="outline-danger"
          :disabled="removingId === member.membership_id"
          @click="removeMember(member)"
        >
          Quitar
        </b-button>
      </li>
    </ul>

    <h5 class="h6">Agregar integrante</h5>
    <p class="text-muted small">Se cobra el prorrateo con la tarjeta guardada. Mínimo 2 personas, contando al titular.</p>
    <b-row>
      <b-col cols="12" md="6" class="mb-2">
        <label class="small mb-1">Nombre</label>
        <b-form-input v-model.trim="form.name" />
      </b-col>
      <b-col cols="12" md="6" class="mb-2">
        <label class="small mb-1">Apellido</label>
        <b-form-input v-model.trim="form.lastname" />
      </b-col>
      <b-col cols="12" md="4" class="mb-2">
        <label class="small mb-1">Fecha de nacimiento</label>
        <b-form-input v-model="form.date_birth" placeholder="MM-DD-YYYY" />
      </b-col>
      <b-col cols="12" md="4" class="mb-2">
        <label class="small mb-1">Parentesco</label>
        <b-form-select v-model="form.relationship" :options="relationships" />
      </b-col>
      <b-col v-if="showContact" cols="12" md="4" class="mb-2">
        <label class="small mb-1">Teléfono</label>
        <b-form-input v-model.trim="form.phone" />
      </b-col>
      <b-col v-if="showContact" cols="12" md="4" class="mb-2">
        <label class="small mb-1">Correo</label>
        <b-form-input v-model.trim="form.email" type="email" />
      </b-col>
    </b-row>
    <p v-if="audienceHint" class="small text-muted">{{ audienceHint }}</p>
    <b-button variant="primary" :disabled="saving" @click="addMember">
      {{ saving ? 'Guardando...' : 'Agregar integrante' }}
    </b-button>
  </div>
</template>

<script>
import { BAlert, BButton, BCol, BFormInput, BFormSelect, BRow, BSpinner } from 'bootstrap-vue'
import memberService from '@/core/services/api/member'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import {
  ageFromDateBirth,
  emptyFamilyMember,
  familyAgeLimits,
  familyMemberPayload,
  isFamilyHolder,
  memberAudience,
  RELATIONSHIP_OPTIONS,
  unwrapList,
  unwrapRecord,
  validateFamilyMember,
} from '@/core/utils/membershipFamily'

/**
 * Grupo familiar del titular: ver, agregar y quitar integrantes.
 */
export default {
  name: 'MemberFamilyPanel',
  components: { BAlert, BButton, BCol, BFormInput, BFormSelect, BRow, BSpinner },
  props: {
    membershipId: { type: [Number, String], required: true },
    plan: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      removingId: null,
      errorMessage: null,
      pendingInvoiceUrl: null,
      members: [],
      form: emptyFamilyMember(),
      relationships: RELATIONSHIP_OPTIONS,
    }
  },
  computed: {
    ageLimits() {
      return familyAgeLimits(this.plan)
    },
    audience() {
      return memberAudience(ageFromDateBirth(this.form.date_birth), this.ageLimits)
    },
    showContact() {
      return this.audience !== 'child' && this.audience !== 'underage'
    },
    audienceHint() {
      if (this.audience === 'child') return `Los niños de ${this.ageLimits.childMinAge} a ${this.ageLimits.adultMinAge - 1} años no reciben acceso ni correo.`
      if (this.audience === 'underage') return `Los menores de ${this.ageLimits.childMinAge} años no pueden entrar al plan.`
      if (this.audience === 'adult') return 'Si escribes un correo, esa persona recibe su propio acceso.'
      return ''
    },
  },
  watch: {
    membershipId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },
  methods: {
    audienceLabel(member) {
      if (isFamilyHolder(member)) return 'Titular'
      if (member.audience === 'child' || member.member_type === 'child') return 'Niño'
      return 'Adulto'
    },
    canRemove(member) {
      if (member.removed_at) return false
      const active = this.members.filter((item) => !item.removed_at)
      if (active.length <= 2) return false
      return !isFamilyHolder(member)
    },
    async load() {
      if (!this.membershipId) return
      this.loading = true
      this.errorMessage = null
      try {
        const response = await memberService.getFamily(this.membershipId)
        this.members = unwrapList(response)
      } catch (error) {
        this.members = []
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar el grupo familiar.')
      } finally {
        this.loading = false
      }
    },
    async addMember() {
      const localError = validateFamilyMember(this.form, 'Integrante', this.ageLimits)
      if (localError) {
        this.errorMessage = localError
        return
      }
      this.saving = true
      this.errorMessage = null
      this.pendingInvoiceUrl = null
      try {
        const response = await memberService.addFamilyMember(this.membershipId, familyMemberPayload(this.form, this.ageLimits))
        const record = unwrapRecord(response) || {}
        if (record.state === 'pending_payment' && record.pending_invoice_url) {
          this.pendingInvoiceUrl = record.pending_invoice_url
        }
        this.form = emptyFamilyMember()
        await this.load()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo agregar al integrante.')
      } finally {
        this.saving = false
      }
    },
    async removeMember(member) {
      this.removingId = member.membership_id
      this.errorMessage = null
      try {
        const response = await memberService.removeFamilyMember(this.membershipId, member.membership_id)
        const next = unwrapList(response)
        this.members = next.length ? next : this.members
        if (!next.length) await this.load()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo quitar al integrante.')
      } finally {
        this.removingId = null
      }
    },
  },
}
</script>
